import { Button, Grid, TextField, styled } from '@mui/material';
import React from 'react';
import { Formik, FormikErrors } from 'formik';
import { PartialUser } from 'src/dto/User';
import { AlertBox, FormMessage } from 'src/components/AlertBox';
import { useUpdatePartialUserMutation } from 'src/redux/services/users';

import countries from 'src/components/PhoneInput/countries';
import { Heading, Subtitle, Title } from 'src/pages/Login/forms/styledFormComponents';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { AsYouType } from 'libphonenumber-js';
import { useInitialPrefix } from 'src/hooks';
import { getAccessToken } from 'src/frontend-common/localstorage';

interface UserFormValues {
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  phone: string;
  phone_country_code: string;
}

export interface UserFormProps {
  userData: Pick<PartialUser, 'id' | 'first_name' | 'last_name' | 'company' | 'email' | 'phone' | 'phone_country_code'>;
}

const Form = styled('form')(() => ({
  width: '100%',
  maxWidth: 500,
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(7),
}));

const CustomButton = styled(Button)(() => ({
  height: 56,
  width: '100%',
}));

export const PhoneErrorWrapper = styled('div')(() => ({
  fontWeight: 400,
  fontSize: '0.75rem',
  color: '#F44336',
  lineHeight: 1.66,
  letterSpacing: '0.03333em',
  textAlign: 'left',
  marginTop: 3,
  marginRight: 14,
  marginBottom: 0,
  marginLeft: 14,
}));

export const getCountryCodeFromIso2 = (iso2: string) => `+${countries.find((el) => el.iso2 === iso2)?.value}`;

export const CompleteUserForm: React.FC<UserFormProps> = ({ userData }) => {
  const [errorMessages, setErrorMessages] = React.useState<FormMessage[]>([]);
  const [updateUser, { isLoading }] = useUpdatePartialUserMutation();

  const initialPrefix = useInitialPrefix();
  const [phone_country_code, setPhoneCountryCode] = React.useState<string | undefined>();

  const handleErrorMessageDismiss = (i: number) => {
    const newErrorMessages = [...errorMessages];
    newErrorMessages.splice(i, 1);
    setErrorMessages(newErrorMessages);
  };

  const getCountryCode = (input: string) => {
    // Set default country code to US if no real country code is specified
    const defaultCountryCode = input.substr(0, 1) !== '+' ? 'US' : null;
    let formatted = new AsYouType('US').input(input);
    let countryCode: string | undefined = '';
    let withoutCountryCode = formatted;

    if (defaultCountryCode === 'US') {
      countryCode = '+1';
      formatted = '+1 ' + formatted;
    } else {
      const parts = formatted.split(' ');
      countryCode = parts.length > 1 ? parts.shift() : '';
      withoutCountryCode = parts.join(' ');
    }

    return {
      formatted,
      withoutCountryCode,
      countryCode,
    };
  };

  React.useEffect(() => {
    if (userData?.phone) {
      setPhoneCountryCode(getCountryCode(userData.phone).countryCode);
    }
  }, []);

  const handleValidation = (values: UserFormValues): FormikErrors<UserFormValues> => {
    const errors: FormikErrors<UserFormValues> = {};

    if (values.first_name?.length === 0) {
      errors.first_name = 'Required';
    }
    if (values.last_name?.length === 0) {
      errors.last_name = 'Required';
    }
    if (values.company_name?.length === 0) {
      errors.company_name = 'Required';
    }
    if (values.email?.length === 0) {
      errors.email = 'Required';
    }
    if (values?.phone && !values.phone.match('^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$')) {
      errors.phone = 'Invalid phone number';
    }
    if (values?.phone?.trim().length === 0) {
      errors.phone = 'Required';
    }
    return errors;
  };

  return (
    <>
      <Heading style={{ display: 'block' }}>
        <Title
          style={{
            textAlign: 'initial',
            fontSize: 40,
            fontWeight: 600,
          }}
        >
          Complete your profile
        </Title>
        <Subtitle variant="body2" style={{ textAlign: 'initial' }}>
          Please complete your profile with the missing personal info.
        </Subtitle>
      </Heading>
      <AlertBox messages={errorMessages} onMessageDismiss={handleErrorMessageDismiss} />
      <Formik
        initialValues={{
          first_name: userData?.first_name ?? '',
          last_name: userData?.last_name ?? '',
          company_name: userData?.company?.name ?? '',
          phone: userData?.phone ?? '',
          phone_country_code: userData?.phone_country_code ?? '',
          email: userData?.email ?? '',
          __all: '',
        }}
        validateOnChange={true}
        validate={handleValidation}
        enableReinitialize
        onSubmit={async (values: UserFormValues, { setErrors }) => {
          setErrorMessages([]);
          const result = await updateUser({
            id: userData.id,
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            company_name: values.company_name ?? '',
            phone: values.phone?.includes('+') ? values.phone : `+${values.phone}`,
            phone_country_code: values.phone_country_code ? getCountryCodeFromIso2(values.phone_country_code) : phone_country_code || undefined,
          });
          if ('error' in result && 'data' in result.error) {
            const violations = // @ts-ignore:next-line
              result.error?.data?.violations as FormikErrors<UserFormValues>;
            setErrors({ ...violations });
            return;
          }
          const token = getAccessToken();
          if (token) {
            const setUrl = (window.location.href = '/#access_token=' + token);
            setTimeout(setUrl, 500);
          }
        }}
      >
        {(formikProps) => {
          const {
            errors: { first_name: firstNameError, last_name: lastNameError, company_name: companyNameError, email: emailError },
            touched: { first_name: firstNameTouched, last_name: lastNameTouched, company_name: companyNameTouched, email: emailTouched },
          } = formikProps;

          return (
            <Form
              style={{ width: '100%', maxWidth: '100%' }}
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formikProps.handleSubmit();
              }}
            >
              <Grid container spacing={3}>
                <Grid item md={12} xs={12}>
                  <CustomTextField
                    variant="outlined"
                    fullWidth={true}
                    style={{ marginBottom: 0 }}
                    label="Work email"
                    error={emailTouched && !!emailError}
                    helperText={emailTouched && emailError}
                    {...formikProps.getFieldProps('email')}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    variant="outlined"
                    fullWidth={true}
                    style={{ marginBottom: 0 }}
                    label="First name"
                    error={firstNameTouched && !!firstNameError}
                    helperText={firstNameTouched && firstNameError}
                    {...formikProps.getFieldProps('first_name')}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    variant="outlined"
                    fullWidth={true}
                    style={{ marginBottom: 0 }}
                    label="Last name"
                    error={lastNameTouched && !!lastNameError}
                    helperText={lastNameTouched && lastNameError}
                    {...formikProps.getFieldProps('last_name')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PhoneInput
                    dropdownStyle={{ position: 'fixed' }}
                    country={initialPrefix.toLowerCase() || formikProps.getFieldProps('phone_prefix').value}
                    enableSearch
                    value={formikProps.getFieldProps('phone').value}
                    isValid={!formikProps.errors['phone']}
                    onChange={(phone, obj: CountryData) => {
                      formikProps.setFieldValue('phone_country_code', obj.countryCode, true);
                      formikProps.setFieldValue('phone', phone, true);
                    }}
                    inputStyle={{ width: '100%', height: 53 }}
                  />
                  {formikProps.errors['phone'] && <PhoneErrorWrapper>{formikProps.errors['phone']}</PhoneErrorWrapper>}
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    variant="outlined"
                    fullWidth={true}
                    disabled={!!formikProps.initialValues.company_name}
                    label="Company name"
                    error={companyNameTouched && !!companyNameError}
                    helperText={companyNameTouched && companyNameError}
                    {...formikProps.getFieldProps('company_name')}
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <CustomButton
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={isLoading}
                    style={{ backgroundColor: 'black', color: 'white' }}
                    onClick={() => formikProps.submitForm()}
                  >
                    Done
                  </CustomButton>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
